import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ReconSocialProfileEntity } from './recon-social-profile.entity';

/**
 * Detected signals from social platforms.
 * Raw content that may become opportunities after scoring.
 */
@Entity('recon_signals')
@Index('idx_recon_signals_unique', ['platformId', 'platformContentId'], {
	unique: true,
})
@Index('idx_recon_signals_status', ['status'])
@Index('idx_recon_signals_detected', ['detectedAt'])
export class ReconSignalEntity extends BaseEntity {
	// Source identification
	@Column('varchar', { name: 'platform_id', length: 50 })
	public platformId!: string;

	@Column('varchar', { name: 'platform_content_id', length: 500 })
	public platformContentId!: string;

	@Column('text', { name: 'platform_content_url', nullable: true })
	public platformContentUrl!: string | null;

	@Column('varchar', {
		name: 'platform_author_id',
		length: 255,
		nullable: true,
	})
	public platformAuthorId!: string | null;

	@Column('varchar', {
		name: 'platform_author_name',
		length: 255,
		nullable: true,
	})
	public platformAuthorName!: string | null;

	@Column('varchar', {
		name: 'platform_author_handle',
		length: 255,
		nullable: true,
	})
	public platformAuthorHandle!: string | null;

	@Column('int', { name: 'social_profile_id', unsigned: true, nullable: true })
	public socialProfileId!: number | null;

	// Content
	/**
	 * Content type.
	 * 'post', 'comment', 'reply', 'thread'
	 */
	@Column('varchar', { name: 'content_type', length: 50 })
	public contentType!: string;

	@Column('text', { name: 'content_text' })
	public contentText!: string;

	@Column('json', { name: 'content_media_urls', nullable: true })
	public contentMediaUrls!: string[] | null;

	@Column('varchar', { name: 'parent_content_id', length: 500, nullable: true })
	public parentContentId!: string | null;

	// Classification
	/**
	 * Signal type detected by AI.
	 * 'pain_point', 'buying_intent', 'competitor_mention', 'question', 'recommendation_request', etc.
	 */
	@Column('varchar', { name: 'signal_type', length: 50, nullable: true })
	public signalType!: string | null;

	@Column('json', { name: 'matched_keywords', nullable: true })
	public matchedKeywords!: string[] | null;

	/**
	 * Platform-specific engagement metrics.
	 * { likes, replies, reposts, views }
	 */
	@Column('json', { name: 'platform_metrics', nullable: true })
	public platformMetrics!: Record<string, number> | null;

	// Timing
	@Column('datetime', { name: 'published_at', nullable: true })
	public publishedAt!: Date | null;

	@Column('datetime', {
		name: 'detected_at',
		default: () => 'CURRENT_TIMESTAMP',
	})
	public detectedAt!: Date;

	// Processing state
	/**
	 * Processing status.
	 * 'new', 'enriching', 'scored', 'queued', 'dismissed'
	 */
	@Column('varchar', { name: 'status', length: 30, default: 'new' })
	public status!: string;

	@Column('int', { name: 'opportunity_id', unsigned: true, nullable: true })
	public opportunityId!: number | null;

	// Relations
	@ManyToOne(() => ReconSocialProfileEntity)
	@JoinColumn({ name: 'social_profile_id' })
	public socialProfile?: ReconSocialProfileEntity;
}
